import React, { useCallback, useEffect, useState } from "react";
import { Row, Label, Input, Form, FormGroup, Button, Col } from "reactstrap";
import { useFormik } from "formik";
import * as yup from "yup";
// import imageCompression from "browser-image-compression";
//Import Flatepicker
import "flatpickr/dist/themes/material_blue.css";
import { useDropzone } from "react-dropzone";
import {
  completeUpload,
  getDocumentsTypes,
  uploadChunk,
} from "../../../helpers/AuthType/authBackend";
import { setAuthorization } from "../../../helpers/api_helper";
import { getPublicCountries } from "../../../helpers/dataBackend";
import { splitFileIntoChunksBase64 } from "shared/fileUtils";
import { BiLoaderAlt, BiCheckCircle } from "react-icons/bi";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

interface UploadState {
  loading: boolean;
  validated: boolean;
}

interface IdentityInformationProps {
  onSubmit: (values: any) => void;
  onBack: () => void;
  data?: any;
  userId: any;
}

const IdentityVerification = ({
  onSubmit,
  onBack,
  data,
  userId,
}: IdentityInformationProps) => {
  //meta title
  document.title = "Ditobanx";

  type DocType = "Passport" | "front" | "back" | "selfie";

  const [selectedFileFront, setSelectedFileFront] = useState<any[]>([]);
  const [selectedFileBack, setSelectedFileBack] = useState<any[]>([]);
  const [selectedSelfie, setSelectedSelfie] = useState<any[]>([]);
  const [documentType, setDocumentType] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [typeDocument, setTypeDocument] = useState<any>("");

  //Estados para el loading
  const initialStatus: Record<DocType, UploadState> = {
    Passport: { loading: false, validated: false },
    front: { loading: false, validated: false },
    back: { loading: false, validated: false },
    selfie: { loading: false, validated: false },
  };
  const [status, setStatus] =
    useState<Record<DocType, UploadState>>(initialStatus);

  const setLoading = (type: DocType, loading: boolean) =>
    setStatus((s) => ({ ...s, [type]: { ...s[type], loading } }));

  const setValidated = (type: DocType, validated: boolean) =>
    setStatus((s) => ({ ...s, [type]: { ...s[type], validated } }));

  const resetStatus = (type: DocType) =>
    setStatus((s) => ({
      ...s,
      [type]: { loading: false, validated: false },
    }));

  // Función para el documento (permite varios archivos)
  const onDropDocsFront = useCallback((acceptedFiles: any[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (2 MB).`
        );
        return false;
      }
      return true;
    });

    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    // Se agregan al estado sin eliminar los anteriores
    setSelectedFileFront((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  // Función para el documento (permite varios archivos)
  const onDropDocsBack = useCallback((acceptedFiles: any[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (2 MB).`
        );
        return false;
      }
      return true;
    });

    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    // Se agregan al estado sin eliminar los anteriores
    setSelectedFileBack((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  // Función para la selfie (solo permite un archivo)
  const onDropSelfie = useCallback((acceptedFiles: any[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (2 MB).`
        );
        return false;
      }
      return true;
    });

    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    // Se sobreescribe el estado, limitando a 1 archivo
    setSelectedSelfie(mappedFiles.slice(0, 1));
  }, []);

  // Dropzone para documentos FRON - PASSWORD
  const { getInputProps: getDocInputProps, open: openDocFront } = useDropzone({
    onDrop: onDropDocsFront,
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [],
    },
    maxFiles: 1,
  });

  // Dropzone para documentos BACK
  const { getInputProps: getDocInputPropsBack, open: openDocBack } =
    useDropzone({
      onDrop: onDropDocsBack,
      noClick: true,
      noKeyboard: true,
      accept: {
        "image/*": [],
      },
      maxFiles: 1,
    });

  // Dropzone para selfie (se limita a un solo archivo)
  const { getInputProps: getSelfieInputProps, open: openSelfie } = useDropzone({
    onDrop: onDropSelfie,
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [],
    },
    maxFiles: 1,
  });

  // Función para eliminar un documento por índice
  const removeDoc = (index: number) => {
    setSelectedFileFront((prev) => prev.filter((_, i) => i !== index));
    resetStatus("Passport"); // si estabas usando Passport
    resetStatus("front"); // o "front" según tu flujo
  };

  // Función para eliminar un documento por índice
  const removeDocBack = (index: number) => {
    setSelectedFileBack((prev) => prev.filter((_, i) => i !== index));
    resetStatus("back");
  };

  // Función para eliminar la selfie (se limpia el arreglo)
  const removeSelfie = (index: number) => {
    setSelectedSelfie((prev) => prev.filter((_, i) => i !== index));
    resetStatus("selfie"); // ← vuelve a “no validado”
  };

  const uploadFileChunks = async (file: File, type: string) => {
    const chunks = await splitFileIntoChunksBase64(file);
    const uploadId = `${file.name}-${Date.now()}`;
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      const payload = {
        uploadId,
        filename: file.name,
        chunkIndex: i,
        totalChunks,
        chunkBase64: chunks[i],
        type, // Ej: "selfie", "front", etc.
      };

      await uploadChunk(payload);
    }

    // Notificamos que se terminó de subir
    await completeUpload({
      uploadId,
      filename: file.name,
      type,
      userId,
    });

    return uploadId;
  };

  const validarPhotoUploaded = async (type: DocType) => {
    const file =
      type === "back"
        ? selectedFileBack[0]
        : type === "selfie"
        ? selectedSelfie[0]
        : type === "Passport"
        ? selectedFileFront[0]
        : selectedFileFront[0];

    if (!file) return;

    try {
      setLoading(type, true);
      await uploadFileChunks(file, type);
      setValidated(type, true); // marca como validado
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error al subir la imagen");
    } finally {
      setLoading(type, false);
    }
  };

  const getData = async () => {
    const responsePublicToken = localStorage.getItem("publicToken");
    setAuthorization(responsePublicToken);
    const responseDocuments = await getDocumentsTypes();
    // console.log("response", responseDocuments);
    const responseCountries = await getPublicCountries();
    // console.log(JSON.parse(responseDocuments.data));
    setCountries(JSON.parse(responseCountries.data));
    setDocumentType(JSON.parse(responseDocuments.data));
  };

  // Fecha de hoy y fecha límite para 18 años
  const today = new Date();
  const isoToday = today.toISOString().split("T")[0];
  const minAdultDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const formik: any = useFormik({
    initialValues: {
      document_type:
        (data.identityInfo && data.identityInfo.document_type) || "",
      date_of_birth:
        (data.identityInfo && data.identityInfo.date_of_birth) || "",
      document_number:
        (data.identityInfo && data.identityInfo.document_number) || "",
      nationality: (data.identityInfo && data.identityInfo.nationality) || "",
    },
    validationSchema: yup.object().shape({
      document_type: yup.string().required("Select a Document Type"),
      date_of_birth: yup
        .date()
        .required("Ingresa tu fecha de nacimiento")
        // 1. No puede ser posterior a hoy
        .max(today, "La fecha no puede estar en el futuro")
        // 2. Debe ser al menos hace 18 años
        .max(minAdultDate, "Debes tener al menos 18 años"),
      document_number: yup
        .string()
        .required("Enter your document number")
        .matches(
          /^[a-zA-Z0-9:_-]+$/,
          "Solo se permiten letras, números, dos puntos (:), guiones (-) y guiones bajos (_)"
        ),
      nationality: yup.string().required("Enter your Nationality"),
    }),
    onSubmit: async (values: any) => {
      // Flags actuales
      const passportOk = status.Passport.validated;
      const frontOk = status.front.validated;
      const backOk = status.back.validated;
      const selfieOk = status.selfie.validated;

      if (typeDocument === "PAS") {
        // Pasaporte + selfie
        if (passportOk && selfieOk) {
          onSubmit(values); // ← tu callback real
        } else {
          alert(
            "Por favor verifica que el pasaporte y la selfie han sido validados."
          );
        }
      } else {
        // Frente + dorso + selfie
        if (frontOk && backOk && selfieOk) {
          onSubmit(values);
        } else {
          alert(
            "Por favor verifica que frente, dorso y selfie han sido validados."
          );
        }
      }
    },
  });

  useEffect(() => {
    getData();
  }, []);

  const VerifyButton = ({ type, label }: { type: DocType; label: string }) =>
    !!(
      (type === "Passport" && selectedFileFront.length) ||
      (type === "front" && selectedFileFront.length) ||
      (type === "back" && selectedFileBack.length) ||
      (type === "selfie" && selectedSelfie.length)
    ) && (
      <Button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          validarPhotoUploaded(type);
        }}
        disabled={status[type].validated || status[type].loading}
        color={status[type].validated ? "success" : "primary"}
        className="flex items-center gap-2"
      >
        {status[type].loading && <BiLoaderAlt className="animate-spin" />}
        {status[type].validated && !status[type].loading && (
          <BiCheckCircle className="text-green-500" />
        )}
        {status[type].loading
          ? "Cargando…"
          : status[type].validated
          ? "Validado"
          : label}
      </Button>
    );

  return (
    <React.Fragment>
      <Form onSubmit={formik.handleSubmit} autoComplete="off">
        <Row>
          <Col lg={6} md={6} sm={12}>
            {/* TIPO DOCUMENTO */}
            <FormGroup className="mb-3">
              <Label htmlFor="document_type"> Tipo de Documento </Label>
              <select
                className="form-control"
                name="document_type"
                id="document_type"
                value={formik.values.document_type || ""}
                onChange={(e) => {
                  formik.handleChange(e);
                  setTypeDocument(e.target.value);
                }}
              >
                <option value="" selected disabled>
                  Elige una opcion
                </option>
                {documentType.map((item: any) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
              {formik.errors.document_type && formik.touched.document_type ? (
                <span className="text-danger">
                  {formik.errors.document_type}
                </span>
              ) : null}
            </FormGroup>
            {/* Fecha de Nacimiento */}
            <FormGroup className="mb-3">
              <Label htmlFor="date_of_birth">Fecha de Nacimiento</Label>
              <Input
                type="date"
                className="form-control d-block"
                id="date_of_birth"
                name="date_of_birth"
                placeholder="Selecciona fecha"
                // impide elegir > hoy
                max={isoToday}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.date_of_birth || ""}
              />
              {formik.touched.date_of_birth && formik.errors.date_of_birth ? (
                <span className="text-danger">
                  {formik.errors.date_of_birth}
                </span>
              ) : null}
            </FormGroup>
          </Col>
          <Col lg={6} md={6} sm={12}>
            {/* DOCUMENT NUMBER */}
            <FormGroup className="mb-3">
              <Label htmlFor="document_number"> Número de documento </Label>
              <Input
                className="form-control"
                name="document_number"
                id="document_number"
                value={formik.values.document_number || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9:_-]*$/.test(value)) {
                    formik.setFieldValue("document_number", value);
                  }
                }}
                placeholder="Enter your document number"
                onBlur={formik.handleBlur}
              />
              {formik.touched.document_number &&
              formik.errors.document_number ? (
                <span className="text-danger">
                  {formik.errors.document_number}
                </span>
              ) : null}
            </FormGroup>
            {/* NACIONALIDAD */}
            <FormGroup className="mb-3">
              <Label htmlFor="nationality"> Nacionalidad </Label>
              <select
                className="form-control"
                name="nationality"
                id="nationality"
                value={formik.values.nationality || ""}
                onChange={formik.handleChange}
              >
                <option value="" selected disabled>
                  Elige una opcion
                </option>
                {countries.map((item) => (
                  <option id={item.code_iso_31661} value={item.code_iso_31661}>
                    {item.name}
                  </option>
                ))}
              </select>
              {formik.errors.nationality && formik.touched.nationality ? (
                <span className="text-danger">{formik.errors.nationality}</span>
              ) : null}
            </FormGroup>
          </Col>
          {/* UPLOAD */}
          {/* <Row style={{ marginTop: "20px" }}> */}
          <Col lg={6} md={6} sm={6}>
            {typeDocument === "PAS" ? (
              <Row lg={12} md={12} sm={12}>
                <FormGroup className="mb-3">
                  <Label htmlFor="documento-input">Pasaporte</Label>
                  <p>Una vez cargado el documento debes darle verificar</p>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <i
                      className="bx bx-upload"
                      style={{ fontSize: "40px" }}
                    ></i>
                    <h5 className="font-size-14 mb-3">
                      Selecciona una imagen, tamaño maximo de 2MB
                    </h5>
                    {/* Input controlado por Dropzone */}
                    <input {...getDocInputProps()} />
                    <Button
                      type="button"
                      color={
                        selectedFileFront.length >= 1 ? "danger" : "primary"
                      }
                      disabled={selectedFileFront.length >= 1}
                      onClick={(e) => {
                        e.preventDefault();
                        openDocFront();
                      }}
                    >
                      Subir Archivo
                    </Button>

                    {/* Previsualización para documento */}
                    <div
                      style={{
                        marginTop: "20px",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      {selectedFileFront.map((file, index) => (
                        <div
                          key={index}
                          style={{ position: "relative", marginBottom: "10px" }}
                        >
                          {/* Botón para eliminar */}
                          <button
                            type="button"
                            onClick={() => removeDoc(index)}
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              backgroundColor: "red",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "25px",
                              height: "25px",
                              cursor: "pointer",
                            }}
                          >
                            &times;
                          </button>
                          {/* {!documentValidated1 ? (
                          ) : null} */}
                          <img
                            src={file.preview}
                            alt={file.name}
                            style={{
                              maxWidth: "100%",
                              height: "200px",
                              borderRadius: "8px",
                            }}
                          />
                          <p>{file.name}</p>
                        </div>
                      ))}
                    </div>
                    {selectedFileFront.length > 0 ? (
                      <VerifyButton type="Passport" label="Verificar Imagen" />
                    ) : null}
                  </div>
                </FormGroup>
              </Row>
            ) : (
              <Row lg={12} md={12} sm={12}>
                <FormGroup className="mb-3">
                  <Label htmlFor="documento-input">
                    Documendo de identidad (Frente)
                  </Label>
                  <p>Una vez cargado el documento debes darle verificar</p>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <i
                      className="bx bx-upload"
                      style={{ fontSize: "40px" }}
                    ></i>
                    <h5 className="font-size-14 mb-3">
                      Selecciona una imagen, tamaño maximo de 2MB
                    </h5>
                    {/* Input controlado por Dropzone */}
                    <input {...getDocInputProps()} />
                    <Button
                      type="button"
                      color={
                        selectedFileFront.length >= 1 ? "danger" : "primary"
                      }
                      disabled={selectedFileFront.length >= 1}
                      onClick={(e) => {
                        e.preventDefault();
                        openDocFront();
                      }}
                    >
                      Subir Archivo
                    </Button>

                    {/* Previsualización para documento */}
                    <div
                      style={{
                        marginTop: "20px",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      {selectedFileFront.map((file, index) => (
                        <div
                          key={index}
                          style={{ position: "relative", marginBottom: "10px" }}
                        >
                          {/* Botón para eliminar */}
                          <button
                            type="button"
                            onClick={() => removeDoc(index)}
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              backgroundColor: "red",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "25px",
                              height: "25px",
                              cursor: "pointer",
                            }}
                          >
                            &times;
                          </button>
                          {/* {!frontValidated ? (
                          ) : null} */}
                          <img
                            src={file.preview}
                            alt={file.name}
                            style={{
                              maxWidth: "100%",
                              height: "200px",
                              borderRadius: "8px",
                            }}
                          />
                          <p>{file.name}</p>
                        </div>
                      ))}
                    </div>
                    {selectedFileFront.length > 0 ? (
                      <VerifyButton type="front" label="Verificar Frente" />
                    ) : null}
                  </div>
                </FormGroup>

                {/* EMPIEZA EL DORSO */}
                {typeDocument !== "PAS" ? (
                  <FormGroup className="mb-3">
                    <Label htmlFor="documento-input">
                      Documento de identidad DORSO
                    </Label>
                    <p>Una vez cargado el documento debes darle verificar</p>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <i
                        className="bx bx-upload"
                        style={{ fontSize: "40px" }}
                      ></i>
                      <h5 className="font-size-14 mb-3">
                        Selecciona una imagen, tamaño maximo de 2MB
                      </h5>
                      {/* Input controlado por Dropzone */}
                      <input {...getDocInputPropsBack()} />
                      <Button
                        type="button"
                        color={
                          selectedFileBack.length >= 1 ? "danger" : "primary"
                        }
                        disabled={selectedFileBack.length >= 1}
                        onClick={(e) => {
                          e.preventDefault();
                          openDocBack();
                        }}
                      >
                        Subir Archivo
                      </Button>

                      {/* Previsualización para documento */}
                      <div
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {selectedFileBack.map((file, index) => (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              marginBottom: "10px",
                            }}
                          >
                            {/* Botón para eliminar */}
                            <button
                              type="button"
                              onClick={() => removeDocBack(index)}
                              style={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                backgroundColor: "red",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "25px",
                                height: "25px",
                                cursor: "pointer",
                              }}
                            >
                              &times;
                            </button>
                            {/* {!backValidated ? (
                            ) : null} */}
                            <img
                              src={file.preview}
                              alt={file.name}
                              style={{
                                maxWidth: "100%",
                                height: "200px",
                                borderRadius: "8px",
                              }}
                            />
                            <p>{file.name}</p>
                          </div>
                        ))}
                      </div>
                      {selectedFileBack.length > 0 ? (
                        <VerifyButton type="back" label="Verificar Dorso" />
                      ) : null}
                    </div>
                  </FormGroup>
                ) : null}
              </Row>
            )}
          </Col>

          {/* SELFIE */}
          <Col lg={6} md={6} sm={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="selfie-input">Selfie para verificación</Label>
              <p>Una vez cargado el documento debes darle verificar</p>
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <i className="bx bx-upload" style={{ fontSize: "40px" }}></i>
                <h5 className="font-size-14 mb-3">
                  Selecciona una imagen, tamaño maximo de 2MB
                </h5>
                {/* Input controlado por Dropzone */}
                <input {...getSelfieInputProps()} />
                <Button
                  type="button"
                  color={selectedSelfie.length >= 1 ? "danger" : "primary"}
                  disabled={selectedSelfie.length >= 1}
                  onClick={(e) => {
                    e.preventDefault();
                    openSelfie();
                  }}
                >
                  Subir Archivo
                </Button>

                {/* Previsualización para documento */}
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {selectedSelfie.map((file, index) => (
                    <div
                      key={index}
                      style={{ position: "relative", marginBottom: "10px" }}
                    >
                      {/* Botón para eliminar */}
                      <button
                        type="button"
                        onClick={() => removeSelfie(index)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "red",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "25px",
                          height: "25px",
                          cursor: "pointer",
                        }}
                      >
                        &times;
                      </button>
                      {/* {!selfieValidated ? (
                      ) : null} */}
                      <img
                        src={file.preview}
                        alt={file.name}
                        style={{
                          maxWidth: "100%",
                          height: "200px",
                          borderRadius: "8px",
                        }}
                      />
                      <p>{file.name}</p>
                    </div>
                  ))}
                </div>
                {selectedSelfie.length > 0 ? (
                  <VerifyButton type="selfie" label="Verificar Selfie" />
                ) : null}
              </div>
            </FormGroup>
          </Col>
        </Row>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {/* <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={onBack}
          >
            Regresar
          </button> */}
          <Button type="submit" className="btn btn-primary">
            Siguiente
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default IdentityVerification;
